// src/components/sdk/components/UsageFilter.tsx

import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Form, DatePicker, Button, Space, Select, Modal, message } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { AppBrief, UserBrief } from '../SdkInterface';
import { sdkMgmt } from '../../../server/SdkMgmt';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface UsageFilterProps {
    t: (key: string) => string;
    usageFilter: any;
    onFilterApply: (values: any) => void;
    onFilterReset: () => void;
    usageFormRef: React.RefObject<any>;
    users?: UserBrief[];
    appBrief?: AppBrief[];
    isMobile?: boolean;
    /** 删除过期记录成功后触发，由外部刷新使用记录列表 */
    onDeleted?: () => void;
}

export const UsageFilter: React.FC<UsageFilterProps> = ({
    t,
    usageFilter,
    onFilterApply,
    onFilterReset,
    usageFormRef,
    users = [],
    appBrief = [],
    isMobile = false,
    onDeleted,
}) => {
    const [delModalOpen, setDelModalOpen] = useState(false);
    const [delExpiryDate, setDelExpiryDate] = useState<any>(null);
    const [delConfirmLoading, setDelConfirmLoading] = useState(false);

    const onDelExpiryRecord = () => {
        // 打开弹框时重置选择的时间
        setDelExpiryDate(null);
        setDelModalOpen(true);
    };

    const handleDelModalOk = async () => {
        if (!delExpiryDate) {
            message.warning(
                t('sdk.usage.filter.delExpiryRecordDateRequired') || '请选择截止时间'
            );
            return;
        }

        // 从 DatePicker 选中的 dayjs 对象中获取时间
        // delExpiryDate 是 dayjs 对象（antd v5 DatePicker 默认值类型）
        const date = dayjs(delExpiryDate).format('YYYY-MM-DD');

        try {
            setDelConfirmLoading(true);
            // 调用服务器接口删除使用记录，将选定的时间作为参数
            const res = await sdkMgmt.delUseRecord(date);

            if (!res?.success) {
                // 优先使用后端返回的 message；没有则用 i18n 兜底文案
                message.error(
                    res?.message ||
                    t('sdk.usage.filter.delExpiryRecordFailed') ||
                    '过期记录删除失败'
                );
                return; // 失败时保持弹框打开，便于用户重试
            }

            message.success(
                t('sdk.usage.filter.delExpiryRecordSuccess') || '过期记录删除成功'
            );
            setDelModalOpen(false);
            // 删除成功后通知外部刷新使用记录列表
            onDeleted?.();
        } catch (error) {
            message.error(
                t('sdk.usage.filter.delExpiryRecordFailed') || '过期记录删除失败'
            );
        } finally {
            setDelConfirmLoading(false);
        }
    };

    return (
        <>
            <Form
                ref={usageFormRef}
                layout={isMobile ? 'vertical' : 'inline'}
                onFinish={onFilterApply}
                initialValues={{
                    userId: usageFilter.userId,
                    appId: usageFilter.appId,
                }}
                style={{
                    marginBottom: 16,
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    background: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid #e8e8e8',
                }}
            >
                {/* 移动端：选择应用和按钮放在一行 */}
                {isMobile ? (
                    <>
                        <div style={{
                            display: 'flex',
                            gap: 8,
                            marginBottom: 8,
                            alignItems: 'center',
                        }}>
                            <Form.Item name="appId" style={{ marginBottom: 0, flex: 1 }}>
                                <Select
                                    placeholder={t('sdk.usage.filter.selectApp') || '选择应用'}
                                    style={{ width: '100%' }}
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {appBrief.map((app) => (
                                        <Option key={app.id} value={app.id}>
                                            {app.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0, flexShrink: 0 }}>
                                <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                                    {t('sdk.refresh') || '过滤'}
                                </Button>
                            </Form.Item>
                            <Form.Item style={{ marginBottom: 0, flexShrink: 0 }}>
                                <Button onClick={onFilterReset}>
                                    {t('sdk.usage.filter.reset') || '重置'}
                                </Button>
                            </Form.Item>
                        </div>
                        <Form.Item name="timeRange" style={{ marginBottom: 0 }}>
                            <RangePicker
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                                placeholder={[
                                    t('sdk.usage.filter.startTime') || '开始时间',
                                    t('sdk.usage.filter.endTime') || '结束时间'
                                ]}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </>
                ) : (
                    // 桌面端保持原样
                    <>
                        <Form.Item name="timeRange" style={{ marginBottom: 0 }}>
                            <RangePicker
                                showTime
                                format="YYYY-MM-DD HH:mm:ss"
                                placeholder={[
                                    t('sdk.usage.filter.startTime') || '开始时间',
                                    t('sdk.usage.filter.endTime') || '结束时间'
                                ]}
                                style={{ width: 340 }}
                            />
                        </Form.Item>
                        <Form.Item name="appId" style={{ marginBottom: 0 }}>
                            <Select
                                placeholder={t('sdk.usage.filter.selectApp') || '选择应用'}
                                style={{ width: 150 }}
                                allowClear
                                showSearch
                                optionFilterProp="children"
                            >
                                {appBrief.map((app) => (
                                    <Option key={app.id} value={app.id}>
                                        {app.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                                    {t('sdk.refresh') || '应用过滤'}
                                </Button>
                                <Button onClick={onFilterReset}>
                                    {t('sdk.usage.filter.reset') || '重置'}
                                </Button>
                                <Button danger onClick={onDelExpiryRecord}>
                                    {t('sdk.usage.filter.delExpiryRecord') || '删除过期记录'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </>
                )}
            </Form>

            <Modal
                open={delModalOpen}
                title={t('sdk.usage.filter.delExpiryRecordConfirmTitle') || '确认删除过期记录'}
                okText={t('confirm') || '确定'}
                okType="danger"
                cancelText={t('cancel') || '取消'}
                confirmLoading={delConfirmLoading}
                onOk={handleDelModalOk}
                onCancel={() => setDelModalOpen(false)}
                destroyOnClose
            >
                <div style={{ marginBottom: 8 }}>
                    {t('sdk.usage.filter.delExpiryRecordConfirmContent') ||
                        '删除后将无法恢复，请选择要删除的过期记录截止时间，确定要删除该时间之前的记录吗？'}
                </div>
                <DatePicker
                    format="YYYY-MM-DD"
                    placeholder={t('sdk.usage.filter.delExpiryRecordDate') || '请选择截止时间'}
                    style={{ width: '100%' }}
                    value={delExpiryDate}
                    onChange={(value) => setDelExpiryDate(value)}
                />
            </Modal>
        </>
    );
};